import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Button,
  cn,
  Input,
  Label,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Textarea,
} from "@nexus/ui";
import type { ScheduledJob, ScheduledJobRecentRun } from "@/features/scheduled-jobs/api/scheduled-jobs.api";
import { usePauseScheduledJob, useResumeScheduledJob, useUpdateScheduledJob } from "@/lib/queries";
import { ScheduledJobMutationError } from "./ScheduledJobMutationError";
import { formatDurationMs } from "@/features/opportunities/lib/format-relative-time";
import {
  EVENT_DETAIL_ROW_GRID,
  EventDetailSection,
} from "@/features/events/components/shared/EventDetailSection";
import { EventStatusBadge } from "@/features/events/components/shared/EventStatusBadge";
import { getPublishedDisplay } from "@/features/events/lib/event-row-display";
import { buildScheduledJobEventsBridgeSearch } from "../lib/events-bridge";
import { advancedFormsEqual, buildAdvancedPayload } from "../lib/scheduled-job-advanced";
import { getScheduledJobCadenceDisplay, isScheduledJobOverdue } from "../lib/scheduled-job-display";
import {
  deriveAdvancedScheduledJobPreview,
  hasEventTypeAttribute,
} from "../lib/scheduled-job-preview";
import {
  advancedFormFromJob,
  emptyAdvancedFormState,
  ScheduledJobAdvancedFields,
  type ScheduledJobAdvancedFormState,
} from "./ScheduledJobAdvancedFields";
import { ScheduledJobHealthBadge } from "./ScheduledJobHealthBadge";
import { ScheduledJobPreview } from "./ScheduledJobPreview";
import { ScheduledJobStateBadge } from "./ScheduledJobStateBadge";
import { isValidCronSchedule, ScheduledJobCronField } from "./ScheduledJobCronField";
import { ScheduledJobTimezoneField } from "./ScheduledJobTimezoneField";

interface ScheduledJobDetailDrawerProps {
  job: ScheduledJob | null;
  now: number;
  /** Slack past a job's next-run time before it flags overdue. From the ops settings. */
  overdueGraceMs?: number;
  /** Streak length at which the health badge turns red. From the ops settings. */
  unhealthyAfterFailures?: number;
  onClose: () => void;
  onRunNow: (jobId: string) => void;
  onRequestDelete: (jobId: string) => void;
  isRunNowPending?: boolean;
}

const EMPTY_VALUE = "—";
/** Tall enough for a real ops note without forcing a scroll inside the field. */
const DESCRIPTION_ROWS = 5;

function DetailRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  const isEmpty = value === EMPTY_VALUE;

  return (
    <div className={EVENT_DETAIL_ROW_GRID}>
      <dt className="text-xs leading-5 text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "leading-5",
          mono ? "break-all font-mono text-[11px]" : "text-xs",
          isEmpty && "text-muted-foreground",
        )}
      >
        {value}
      </dd>
      <dd />
    </div>
  );
}

function RecentRunRow({ run, i18nLanguage }: { run: ScheduledJobRecentRun; i18nLanguage: string }) {
  const { t } = useTranslation();
  const published = getPublishedDisplay(run.publishedAt, i18nLanguage);

  return (
    <div className="flex items-start justify-between gap-3 border-b border-border-subtle py-2 last:border-b-0">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <EventStatusBadge status={run.status} />
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            {run.triggeredBySchedule
              ? t("admin.scheduledJobs.drawer.bySchedule", "scheduler")
              : t("admin.scheduledJobs.drawer.byOperator", "manual")}
          </span>
        </div>
        {run.errorMessage ? (
          <p className="truncate text-[11px] text-error" title={run.errorMessage}>
            {run.errorMessage}
          </p>
        ) : null}
      </div>
      <div className="shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
        <div>{published.time}</div>
        <div>{run.runDurationMs != null ? formatDurationMs(run.runDurationMs) : EMPTY_VALUE}</div>
      </div>
    </div>
  );
}

export function ScheduledJobDetailDrawer({
  job,
  now,
  overdueGraceMs,
  unhealthyAfterFailures,
  onClose,
  onRunNow,
  onRequestDelete,
  isRunNowPending = false,
}: ScheduledJobDetailDrawerProps) {
  const { t, i18n } = useTranslation();
  const update = useUpdateScheduledJob();
  const pause = usePauseScheduledJob();
  const resume = useResumeScheduledJob();

  const [description, setDescription] = useState("");
  const [schedule, setSchedule] = useState("");
  const [timeZone, setTimeZone] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advanced, setAdvanced] = useState<ScheduledJobAdvancedFormState>(emptyAdvancedFormState);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!job) return;
    setDescription(job.description ?? "");
    setSchedule(job.schedule ?? "");
    setTimeZone(job.timeZone ?? "");
    setAdvancedOpen(false);
    setAdvanced(
      advancedFormFromJob({
        topicName: job.pubsubTarget?.topicName,
        attributes: job.pubsubTarget?.attributes,
        bodyJson: job.pubsubTarget?.bodyJson,
        retryConfig: job.retryConfig,
        attemptDeadlineSeconds: job.attemptDeadlineSeconds,
      }),
    );
    setFormError(null);
  }, [
    job?.id,
    job?.description,
    job?.schedule,
    job?.timeZone,
    job?.pubsubTarget,
    job?.retryConfig,
    job?.attemptDeadlineSeconds,
  ]);

  const advancedPreview = useMemo(
    () =>
      deriveAdvancedScheduledJobPreview({
        topicName: advanced.topicName,
        attributesJson: advanced.attributesJson,
        bodyJson: advanced.bodyJson,
      }),
    [advanced.topicName, advanced.attributesJson, advanced.bodyJson],
  );

  const baselineAdvanced = useMemo(
    () =>
      job
        ? advancedFormFromJob({
            topicName: job.pubsubTarget?.topicName,
            attributes: job.pubsubTarget?.attributes,
            bodyJson: job.pubsubTarget?.bodyJson,
            retryConfig: job.retryConfig,
            attemptDeadlineSeconds: job.attemptDeadlineSeconds,
          })
        : emptyAdvancedFormState(),
    [job],
  );

  if (!job) return null;

  // Narrowing from the guard above does not reach the handler bodies below, which
  // close over the prop rather than a const.
  const jobId = job.id;

  const isOverdue = isScheduledJobOverdue(job, now, overdueGraceMs);
  const canRunNow = job.isTriggerable && !!job.eventType;
  const isMutating = update.isPending || pause.isPending || resume.isPending || isRunNowPending;
  const hasPubsubTarget = job.pubsubTarget != null;

  const trimmedDescription = description.trim();
  const nextDescription = trimmedDescription.length > 0 ? trimmedDescription : null;
  const currentDescription = job.description ?? null;
  const isMvpDirty =
    nextDescription !== currentDescription ||
    schedule.trim() !== (job.schedule ?? "") ||
    timeZone.trim() !== (job.timeZone ?? "");
  const isAdvancedDirty = !advancedFormsEqual(advanced, baselineAdvanced);
  const isDirty = isMvpDirty || isAdvancedDirty;

  const canSave =
    isDirty &&
    !isMutating &&
    isValidCronSchedule(schedule) &&
    timeZone.trim().length > 0;

  const mutationError = update.error ?? pause.error ?? resume.error;
  const warnMissingEventType =
    advancedOpen && hasPubsubTarget && !hasEventTypeAttribute(advancedPreview.attributes);

  function advancedFormErrorMessage(
    error: "attributes" | "body" | "retry" | "attemptDeadline" | "incomplete" | "eventType" | "topic",
  ): string {
    switch (error) {
      case "attributes":
        return t(
          "admin.scheduledJobs.advanced.attributesInvalid",
          "Attributes must be a JSON object of string values.",
        );
      case "body":
        return t("admin.scheduledJobs.advanced.bodyInvalid", "Body must be valid JSON.");
      case "retry":
        return t(
          "admin.scheduledJobs.advanced.retryInvalid",
          "Retry fields must be non-negative integers.",
        );
      case "attemptDeadline":
        return t(
          "admin.scheduledJobs.advanced.attemptDeadlineInvalid",
          "Attempt deadline must be an integer between 15 and 1800 seconds.",
        );
      case "eventType":
        return t(
          "admin.scheduledJobs.advanced.eventTypeRequired",
          "Attributes must include a non-empty eventType.",
        );
      case "topic":
        return t(
          "admin.scheduledJobs.advanced.topicInvalid",
          "Topic must be a full resource name starting with projects/.",
        );
      default:
        return t(
          "admin.scheduledJobs.advanced.incomplete",
          "Topic, attributes, and body must be provided together.",
        );
    }
  }

  function saveEdits() {
    if (!canSave) return;
    setFormError(null);

    const advancedResult = buildAdvancedPayload({
      open: advancedOpen,
      form: advanced,
      includePubsub: hasPubsubTarget,
    });
    if (!advancedResult.ok) {
      setFormError(advancedFormErrorMessage(advancedResult.error));
      return;
    }

    update.mutate(
      {
        jobId,
        input: {
          description: nextDescription,
          schedule: schedule.trim(),
          timeZone: timeZone.trim(),
          ...(advancedResult.value ? { advanced: advancedResult.value } : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success(t("admin.scheduledJobs.drawer.saved", "Job updated"));
        },
      },
    );
  }

  function handlePause() {
    pause.mutate(jobId, {
      onSuccess: () => {
        toast.success(t("admin.scheduledJobs.drawer.paused", "Job paused"));
      },
    });
  }

  function handleResume() {
    resume.mutate(jobId, {
      onSuccess: () => {
        toast.success(t("admin.scheduledJobs.drawer.resumed", "Job resumed"));
      },
    });
  }

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent aria-describedby={undefined}>
        <SheetHeader className="gap-0 pr-12">
          <div className="flex flex-wrap items-center gap-2.5">
            <SheetTitle>{job.id}</SheetTitle>
            <ScheduledJobStateBadge state={job.state} />
          </div>
          {trimmedDescription.length > 0 ? (
            <p className="mt-1.5 max-w-[65ch] text-xs leading-5 text-muted-foreground">
              {trimmedDescription}
            </p>
          ) : (
            <p className="mt-1.5 text-xs leading-5 text-muted-foreground/70">
              {t(
                "admin.scheduledJobs.drawer.descriptionEmptyHeader",
                "No description yet — add one below so the next operator knows what this job is for.",
              )}
            </p>
          )}
        </SheetHeader>

        <SheetBody className="flex flex-col gap-6">
          {job.lastAttempt?.outcome === "ERROR" && job.lastAttempt.errorMessage ? (
            <div className="flex flex-col gap-1 rounded-md border border-error-border bg-error-subtle px-4 py-3">
              <span className="text-xs text-error-subtle-foreground">
                {job.lastAttempt.errorMessage}
              </span>
            </div>
          ) : null}

          <EventDetailSection
            label={t("admin.scheduledJobs.drawer.schedule", "Schedule")}
            aside={
              <ScheduledJobHealthBadge
                health={job.health}
                isOverdue={isOverdue}
                unhealthyAfterFailures={unhealthyAfterFailures}
              />
            }
          >
            <dl className="flex flex-col gap-1.5">
              <DetailRow
                label={t("admin.scheduledJobs.drawer.cadence", "Cadence")}
                value={getScheduledJobCadenceDisplay(job)}
                mono
              />
              <DetailRow
                label={t("admin.scheduledJobs.drawer.event", "Event")}
                value={job.eventType ?? EMPTY_VALUE}
                mono
              />
              {job.eventType ? (
                <div className={EVENT_DETAIL_ROW_GRID}>
                  <dt className="text-xs leading-5 text-muted-foreground">
                    {t("admin.scheduledJobs.drawer.history", "History")}
                  </dt>
                  <dd className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs leading-5">
                    <Link
                      to="/admin"
                      search={buildScheduledJobEventsBridgeSearch(job.eventType) as never}
                      className="text-foreground underline-offset-2 hover:underline"
                    >
                      {t("admin.scheduledJobs.drawer.viewEvents", "View events")}
                    </Link>
                    <Link
                      to="/admin"
                      search={
                        buildScheduledJobEventsBridgeSearch(job.eventType, {
                          status: "FAILED",
                        }) as never
                      }
                      className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    >
                      {t("admin.scheduledJobs.drawer.viewFailures", "View failures")}
                    </Link>
                  </dd>
                  <dd />
                </div>
              ) : null}
              <DetailRow
                label={t("admin.scheduledJobs.drawer.nextRun", "Next run")}
                value={
                  job.nextScheduleTime
                    ? new Date(job.nextScheduleTime).toLocaleString(i18n.language)
                    : EMPTY_VALUE
                }
              />
              <DetailRow
                label={t("admin.scheduledJobs.drawer.lastAttempt", "Last attempt")}
                value={
                  job.lastAttempt?.time
                    ? `${new Date(job.lastAttempt.time).toLocaleString(i18n.language)} · ${job.lastAttempt.outcome}`
                    : EMPTY_VALUE
                }
              />
              <DetailRow
                label={t("admin.scheduledJobs.drawer.resourceName", "Resource name")}
                value={job.name}
                mono
              />
            </dl>
          </EventDetailSection>

          <EventDetailSection label={t("admin.scheduledJobs.drawer.edit", "Edit")}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="scheduled-job-edit-description">
                  {t("admin.scheduledJobs.drawer.description", "Description")}
                </Label>
                <Textarea
                  id="scheduled-job-edit-description"
                  value={description}
                  rows={DESCRIPTION_ROWS}
                  disabled={isMutating}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder={t(
                    "admin.scheduledJobs.drawer.descriptionPlaceholder",
                    "What this job does, who owns it, and what to check when it fails.",
                  )}
                  aria-describedby="scheduled-job-edit-description-hint"
                  className="min-h-[7.5rem] resize-y text-xs leading-5"
                />
                <p
                  id="scheduled-job-edit-description-hint"
                  className="text-[11px] leading-4 text-muted-foreground"
                >
                  {t(
                    "admin.scheduledJobs.drawer.descriptionHint",
                    "Shown in the job list and Slack lifecycle alerts. Prefer outcome and owner over restating the cron.",
                  )}
                </p>
              </div>
              <ScheduledJobCronField
                id="scheduled-job-edit-schedule"
                value={schedule}
                disabled={isMutating}
                onChange={setSchedule}
              />
              <div className="sm:max-w-xs">
                <ScheduledJobTimezoneField
                  id="scheduled-job-edit-timezone"
                  value={timeZone}
                  disabled={isMutating}
                  onChange={setTimeZone}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                {t(
                  "admin.scheduledJobs.drawer.renameHint",
                  "Job id cannot be renamed after create.",
                )}
              </p>

              <ScheduledJobAdvancedFields
                idPrefix="scheduled-job-edit"
                open={advancedOpen}
                onOpenChange={setAdvancedOpen}
                value={advanced}
                onChange={setAdvanced}
                disabled={isMutating}
                showPubsubFields={hasPubsubTarget}
              />

              {advancedOpen && hasPubsubTarget ? (
                <ScheduledJobPreview
                  preview={advancedPreview}
                  warnMissingEventType={warnMissingEventType}
                />
              ) : null}

              {formError ? (
                <p className="rounded-md bg-error-subtle px-3 py-2 text-xs text-error-subtle-foreground">
                  {formError}
                </p>
              ) : null}
            </div>
          </EventDetailSection>

          <EventDetailSection label={t("admin.scheduledJobs.drawer.recentRuns", "Recent runs")}>
            {job.recentRuns.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {job.eventType
                  ? t("admin.scheduledJobs.drawer.noRuns", "No runs recorded yet.")
                  : t(
                      "admin.scheduledJobs.drawer.untracked",
                      "This job's target does not decode to a known event, so run history cannot be correlated.",
                    )}
              </p>
            ) : (
              <div className="flex flex-col">
                {job.recentRuns.map((run) => (
                  <RecentRunRow key={run.id} run={run} i18nLanguage={i18n.language} />
                ))}
              </div>
            )}
          </EventDetailSection>

        </SheetBody>

        <SheetFooter className="flex w-full flex-col items-stretch gap-3 px-6">
          {mutationError ? (
            <ScheduledJobMutationError
              error={mutationError}
              onDismiss={() => {
                update.reset();
                pause.reset();
                resume.reset();
              }}
            />
          ) : null}
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 rounded-sm text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
              disabled={isMutating}
              onClick={() => onRequestDelete(job.id)}
            >
              {t("admin.scheduledJobs.drawer.delete", "Delete")}
            </Button>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {job.state === "ENABLED" ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-sm text-xs"
                  disabled={isMutating}
                  onClick={handlePause}
                >
                  {pause.isPending
                    ? t("admin.scheduledJobs.drawer.pausing", "Pausing…")
                    : t("admin.scheduledJobs.drawer.pause", "Pause")}
                </Button>
              ) : null}
              {job.state === "PAUSED" ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-sm text-xs"
                  disabled={isMutating}
                  onClick={handleResume}
                >
                  {resume.isPending
                    ? t("admin.scheduledJobs.drawer.resuming", "Resuming…")
                    : t("admin.scheduledJobs.drawer.resume", "Resume")}
                </Button>
              ) : null}
              {canRunNow ? (
                <Button
                  type="button"
                  variant={isDirty ? "outline" : "default"}
                  size="sm"
                  className="h-8 rounded-sm text-xs"
                  disabled={isMutating}
                  iconStart="terminal"
                  onClick={() => onRunNow(job.id)}
                >
                  {isRunNowPending
                    ? t("admin.scheduledJobs.drawer.runningNow", "Triggering…")
                    : t("admin.scheduledJobs.drawer.runNow", "Run now")}
                </Button>
              ) : null}
              <Button
                type="button"
                variant={isDirty ? "default" : "outline"}
                size="sm"
                className="h-8 rounded-sm text-xs"
                disabled={!canSave}
                onClick={saveEdits}
              >
                {update.isPending
                  ? t("admin.scheduledJobs.drawer.saving", "Saving…")
                  : t("admin.scheduledJobs.drawer.save", "Save changes")}
              </Button>
              <SheetClose asChild>
                <Button variant="ghost" size="sm" className="h-8 rounded-sm text-xs">
                  {t("admin.scheduledJobs.drawer.close", "Close")}
                </Button>
              </SheetClose>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
